import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  BusinessProfileDraft,
  BusinessProfileOfferDraft,
  EvidenceBackedProfileField,
} from '@mouja/domain/analysis';

import { useLanguage } from '../../i18n/language.js';
import { useSession } from '../../workspace/session.js';
import { workspaceCopy, type WorkspaceCopy } from '../../merchant/copy.js';
import {
  clearProfileIntent,
  rememberedIntakeId,
  rememberedProfileIntent,
  useIntakeJourney,
} from '../../merchant/data.js';
import { GeographyCombobox } from '../../merchant/GeographyCombobox.js';
import { domainOf } from '../../merchant/model.js';
import { missingProfileConfirmationFields } from '../../merchant/profileCompleteness.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';
import {
  Button,
  Callout,
  Card,
  DefinitionList,
  EmptyState,
  Field,
  Modal,
  PageHead,
  Pix,
  PlusIcon,
  SoonBadge,
  Tabs,
  Tag,
  useToast,
} from '../../merchant/ui/primitives.js';

type ProfileTab = 'business' | 'offers' | 'taste' | 'sources';
const TABS: readonly ProfileTab[] = ['business', 'offers', 'taste', 'sources'];

function confirmedField(
  field: EvidenceBackedProfileField,
  value: string,
): EvidenceBackedProfileField {
  return {
    ...field,
    value: value.trim() || null,
    basis: 'OWNER_CONFIRMED',
    reviewState: 'OWNER_CONFIRMED',
  };
}

function merchantField(value: string | null): EvidenceBackedProfileField {
  const trimmed = value?.trim() ?? '';
  return {
    value: trimmed.length > 0 ? trimmed : null,
    basis: trimmed.length > 0 ? 'OWNER_CONFIRMED' : 'UNKNOWN',
    confidence: null,
    evidenceIds: [],
    reviewState: trimmed.length > 0 ? 'OWNER_CONFIRMED' : 'UNKNOWN',
  };
}

function newOffer(title: string, description: string): BusinessProfileOfferDraft {
  return {
    localId: `merchant-offer-${String(Date.now())}`,
    title: merchantField(title),
    category: merchantField(null),
    description: merchantField(description),
    audienceOrNeed: merchantField(null),
    deliveryMode: merchantField(null),
    geographyOrAccess: merchantField(null),
    price: merchantField(null),
    proof: [],
    conversionPath: merchantField(null),
  };
}

export function fieldValue(
  field: EvidenceBackedProfileField | undefined,
  fallback: string,
): string {
  return field?.value?.trim() || fallback;
}

/**
 * The Business Profile page from the wireframe: Business, Offers, Creative
 * Taste (coming soon) and Sources, with the edit-details and offer modals.
 *
 * It reads the merchant's current profile (or the intake it was just
 * handed after sign-in) and writes through the intake's correction route —
 * the same durable mutation the journey uses. A merchant arriving from the
 * landing page with a pending "confirm" continues the journey from here.
 */
export function BusinessProfilePage(): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const { session } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const workspace = useWorkspace();
  const { businessName, setBusinessName, setProfileConfirmed } =
    useMerchantWorkspaceIdentity();
  const [params] = useSearchParams();
  const { tab: routeTab } = useParams<{ tab?: string }>();
  const titleId = useId();
  const merchant =
    session.status === 'authenticated' && session.account.role === 'MERCHANT';
  const requestedIntake = params.get('intake') ?? rememberedIntakeId();
  const [modal, setModal] = useState<'edit' | 'offer' | null>(null);
  const [offerEditing, setOfferEditing] = useState<string | null>(null);
  const [continued, setContinued] = useState(false);

  // The intake to read: the one named on the URL, else the workspace's
  // current profile — both are the same server-owned document.
  const intakeId = requestedIntake ?? workspace.profile?.id ?? null;
  const journey = useIntakeJourney(intakeId, {
    merchant,
    onChange: (next) => {
      setBusinessName(next.business.displayName);
      setProfileConfirmed(next.profile?.reviewState === 'APPROVED');
    },
  });
  const view = journey.view ?? workspace.profile;
  const tab: ProfileTab = TABS.includes(routeTab as ProfileTab)
    ? (routeTab as ProfileTab)
    : 'business';
  const workspaceName =
    businessName?.trim() || view?.business.displayName || copy.nav.yourBusiness;

  // A merchant who just signed in from the landing page's "Confirm" lands
  // here with a remembered intent; honour it once, then continue the
  // journey on the Production Line the server created for that intake.
  useEffect(() => {
    if (continued || !merchant || journey.view === null || !journey.view.claimed)
      return;
    const intent = rememberedProfileIntent();
    if (intent === null) return;
    clearProfileIntent();
    setContinued(true);
    const line = workspace.lines.find((item) => item.intakeId === journey.view?.id);
    if (intent === 'confirm' && journey.view.profile?.reviewState !== 'APPROVED') {
      void journey.approve().then((ok) => {
        if (ok && line !== undefined) void navigate(line.href);
      });
      return;
    }
    if (intent === 'correct') setModal('edit');
  }, [continued, journey, merchant, navigate, workspace.lines]);

  const payload = view?.profile?.payload ?? null;
  const readOnly = view?.actions.profileIsReadOnly ?? true;
  const missing = payload === null ? [] : missingProfileConfirmationFields(payload);
  const offers = payload?.offers ?? [];
  const websiteSource = view?.business.sources.find(
    (source) => source.platform === 'WEBSITE',
  );
  const profileTabs = useMemo(
    () =>
      TABS.map((key) => ({
        key,
        label: copy.profile[key],
        href: `/business-profile/${key}`,
        alert:
          key === 'offers' ? offers.length === 0 && payload !== null : key === 'taste',
      })),
    [copy.profile, offers.length, payload],
  );

  const save = async (next: BusinessProfileDraft, reason: string, done: string) => {
    const ok = await journey.correct(next, reason);
    if (ok) {
      toast(done);
      setModal(null);
      setOfferEditing(null);
      void workspace.refresh();
    }
    return ok;
  };

  if (view === null && workspace.loading) {
    return (
      <section className="mj-ws-page" aria-labelledby={titleId}>
        <p className="mj-ws-loading" role="status">
          {copy.common.loading}
        </p>
      </section>
    );
  }

  if (view === null) {
    return (
      <section
        className="mj-ws-page"
        aria-labelledby={titleId}
        data-screen-label="Business Profile"
      >
        <PageHead
          title={copy.profile.title}
          titleId={titleId}
          eyebrow={workspaceName}
        />
        <EmptyState
          className="mj-ws-mt-24"
          title={copy.profile.missing}
          body={copy.profile.missingBody}
          action={
            <Button
              variant="primary"
              onClick={() => void navigate('/production-lines/new')}
            >
              {copy.home.newLine}
            </Button>
          }
        />
      </section>
    );
  }

  return (
    <section
      className="mj-ws-page"
      aria-labelledby={titleId}
      data-screen-label="Business Profile"
    >
      <PageHead
        eyebrow={workspaceName}
        title={copy.profile.title}
        titleId={titleId}
        intro={copy.profile.intro(workspaceName)}
      />

      {journey.error !== null ? (
        <Callout className="mj-ws-mt-20" role="alert">
          <span className="mj-ws-callout-copy mj-ws-sm">{journey.error}</span>
          <Button small onClick={journey.clearError}>
            {copy.common.close}
          </Button>
        </Callout>
      ) : null}

      {payload !== null && offers.length === 0 ? (
        <Callout accent className="mj-ws-mt-20">
          <Pix status="needs-input" />
          <span className="mj-ws-callout-copy mj-ws-sm">
            <strong>{copy.profile.incompleteOffers}</strong>{' '}
            {copy.profile.incompleteOffersBody}
          </span>
          <Button
            small
            disabled={readOnly}
            onClick={() => {
              setOfferEditing(null);
              setModal('offer');
            }}
          >
            {copy.profile.addOffer}
          </Button>
        </Callout>
      ) : null}

      <Tabs label={copy.profile.sections} active={tab} tabs={profileTabs} />

      {tab === 'business' ? (
        <Card role="tabpanel" className="mj-ws-col mj-ws-narrow" padding={24}>
          <DefinitionList
            rows={[
              [
                copy.profile.name,
                fieldValue(payload?.identity, copy.common.notEstablished),
              ],
              [
                copy.profile.description,
                fieldValue(payload?.niche, copy.common.notEstablished),
              ],
              [
                copy.profile.geography,
                fieldValue(payload?.geography, copy.common.notEstablished),
              ],
              [
                copy.profile.website,
                <span key="w" dir="ltr">
                  {websiteSource === undefined
                    ? copy.common.dash
                    : domainOf(websiteSource.canonicalUrl)}
                </span>,
              ],
            ]}
          />
          {missing.length > 0 ? (
            <p className="mj-ws-muted mj-ws-sm">
              {copy.profile.missingFieldsPrefix}{' '}
              {missing.map((field) => localizedMissing(field, language)).join(', ')}
            </p>
          ) : null}
          <div className="mj-ws-row">
            <Button
              disabled={readOnly || payload === null}
              title={readOnly ? copy.profile.readOnly : undefined}
              onClick={() => {
                setModal('edit');
              }}
            >
              {copy.profile.editDetails}
            </Button>
            {view.profile?.reviewState === 'APPROVED' ? (
              <Tag status="complete">{copy.profile.confirmed}</Tag>
            ) : null}
          </div>
        </Card>
      ) : null}

      {tab === 'offers' ? (
        <Card role="tabpanel" padding={0} className="mj-ws-narrow">
          {offers.map((offer) => (
            <div key={offer.localId} className="mj-ws-orow">
              <div className="mj-ws-orow-copy">
                <div className="mj-ws-strong">
                  {fieldValue(offer.title, copy.common.notEstablished)}
                </div>
                <div className="mj-ws-muted mj-ws-sm">
                  {fieldValue(offer.description, '')}
                </div>
              </div>
              <div className="mj-ws-row" style={{ flexWrap: 'nowrap' }}>
                <Button
                  variant="quiet"
                  small
                  disabled={readOnly}
                  aria-label={copy.profile.editAria(fieldValue(offer.title, ''))}
                  onClick={() => {
                    setOfferEditing(offer.localId);
                    setModal('offer');
                  }}
                >
                  {copy.profile.edit}
                </Button>
                <Button
                  variant="quiet"
                  small
                  disabled={readOnly || journey.working}
                  aria-label={copy.profile.removeAria(fieldValue(offer.title, ''))}
                  onClick={() => {
                    if (payload === null) return;
                    const name = fieldValue(offer.title, '');
                    void save(
                      {
                        ...payload,
                        offers: payload.offers.filter(
                          (item) => item.localId !== offer.localId,
                        ),
                      },
                      'Merchant removed an offer.',
                      copy.profile.removed(name),
                    );
                  }}
                >
                  {copy.profile.remove}
                </Button>
              </div>
            </div>
          ))}
          {offers.length === 0 ? (
            <p className="mj-ws-muted mj-ws-sm" style={{ padding: 20 }}>
              {copy.profile.noOffers}
            </p>
          ) : null}
          <div className="mj-ws-card-foot">
            <Button
              small
              disabled={readOnly || payload === null}
              title={readOnly ? copy.profile.readOnly : undefined}
              onClick={() => {
                setOfferEditing(null);
                setModal('offer');
              }}
            >
              <PlusIcon />
              {copy.profile.addOffer}
            </Button>
          </div>
        </Card>
      ) : null}

      {tab === 'taste' ? (
        <div role="tabpanel" className="mj-ws-narrow">
          <Card className="mj-ws-col" padding={24}>
            <div className="mj-ws-eyebrow is-between">
              <span>{copy.line.tasteOptional}</span>
              <SoonBadge />
            </div>
            <h2 className="mj-ws-h2">{copy.profile.tasteEmptyTitle}</h2>
            <p className="mj-ws-muted mj-ws-sm">
              {copy.profile.tasteEmptyBody(workspaceName)}
            </p>
            <div className="mj-ws-taste-rows" aria-hidden="true">
              {copy.profile.tasteRows.map((row) => (
                <div key={row.label} className="mj-ws-taste-row">
                  <span className="mj-ws-taste-label">{row.label}</span>
                  <div className="mj-ws-taste-pair">
                    <span>{row.left}</span>
                    <span>{row.right}</span>
                  </div>
                  <Tag>{copy.common.dash}</Tag>
                </div>
              ))}
            </div>
            <p className="mj-ws-muted mj-ws-sm">
              {copy.profile.tasteApplies(workspaceName)}
            </p>
            <div>
              <Button variant="primary" disabled title={copy.home.hintTaste}>
                {copy.profile.calibrate}
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      {tab === 'sources' ? (
        <Card role="tabpanel" padding={0} className="mj-ws-narrow">
          {view.business.sources.map((source) => (
            <div key={source.canonicalUrl} className="mj-ws-orow">
              <div className="mj-ws-orow-copy">
                <div className="mj-ws-strong">{sourceLabel(source.platform, copy)}</div>
                <div
                  className="mj-ws-muted mj-ws-sm"
                  dir="ltr"
                  style={{ textAlign: 'start' }}
                >
                  {source.externalIdentity ?? source.canonicalUrl}
                </div>
              </div>
              <Tag status="read">{copy.profile.sourceRead}</Tag>
              <Button variant="quiet" small disabled title={copy.profile.refreshSoon}>
                {copy.profile.refresh}
              </Button>
            </div>
          ))}
          {(['INSTAGRAM', 'TIKTOK'] as const)
            .filter(
              (platform) =>
                !view.business.sources.some((source) => source.platform === platform),
            )
            .map((platform) => (
              <div key={platform} className="mj-ws-orow">
                <div className="mj-ws-orow-copy">
                  <div className="mj-ws-strong">{sourceLabel(platform, copy)}</div>
                  <div className="mj-ws-muted mj-ws-sm">{copy.profile.connectSoon}</div>
                </div>
                <Tag>{copy.profile.sourceNotConnected}</Tag>
                <Button variant="quiet" small disabled title={copy.profile.connectSoon}>
                  {copy.profile.connect}
                </Button>
              </div>
            ))}
          <p className="mj-ws-muted mj-ws-sm mj-ws-card-note">
            {copy.profile.sourcesReadOnly}
          </p>
        </Card>
      ) : null}

      {modal === 'edit' && payload !== null ? (
        <EditProfileModal
          copy={copy}
          language={language}
          workspaceName={workspaceName}
          payload={payload}
          busy={journey.working}
          onClose={() => {
            setModal(null);
          }}
          onSave={(next) =>
            void save(
              next,
              'Merchant corrected the generated business profile.',
              copy.profile.saved,
            )
          }
        />
      ) : null}

      {modal === 'offer' && payload !== null ? (
        <OfferModal
          copy={copy}
          busy={journey.working}
          offer={offers.find((offer) => offer.localId === offerEditing) ?? null}
          onClose={() => {
            setModal(null);
            setOfferEditing(null);
          }}
          onSave={(title, description) => {
            const existing = offers.find((offer) => offer.localId === offerEditing);
            const nextOffers =
              existing === undefined
                ? [...payload.offers, newOffer(title, description)]
                : payload.offers.map((offer) =>
                    offer.localId === existing.localId
                      ? {
                          ...offer,
                          title: confirmedField(offer.title, title),
                          description: confirmedField(offer.description, description),
                        }
                      : offer,
                  );
            void save(
              { ...payload, offers: nextOffers },
              existing === undefined
                ? 'Merchant added an offer.'
                : 'Merchant edited an offer.',
              existing === undefined
                ? copy.profile.offerAdded
                : copy.profile.offerUpdated,
            );
          }}
        />
      ) : null}
    </section>
  );
}

function sourceLabel(platform: string, copy: WorkspaceCopy): string {
  if (platform === 'INSTAGRAM') return copy.profile.instagram;
  if (platform === 'TIKTOK') return copy.profile.tiktok;
  return copy.profile.websitePlatform;
}

function localizedMissing(field: string, language: 'en' | 'ar'): string {
  if (language === 'en') return field;
  if (field === 'your business name') return 'اسم نشاطك التجاري';
  if (field === 'what your business does') return 'وش يقدمه نشاطك التجاري';
  if (field === 'where your business operates') return 'وين يشتغل نشاطك';
  if (field === 'at least one concrete offer') return 'عرض فعلي واحد على الأقل';
  return field;
}

export function EditProfileModal({
  copy,
  language,
  workspaceName,
  payload,
  busy,
  onClose,
  onSave,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly workspaceName: string;
  readonly payload: BusinessProfileDraft;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSave: (next: BusinessProfileDraft) => void;
}): ReactNode {
  const ids = { name: useId(), desc: useId(), geo: useId(), offer: useId() };
  const [name, setName] = useState(payload.identity.value ?? '');
  const [description, setDescription] = useState(payload.niche.value ?? '');
  const [geography, setGeography] = useState(payload.geography.value ?? '');
  const firstOffer = payload.offers[0];
  const [offer, setOffer] = useState(firstOffer?.title.value ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title={copy.profile.editTitle}
      description={copy.profile.editBody(workspaceName)}
      onClose={onClose}
      onSubmit={() => {
        if (name.trim().length === 0) {
          setError(copy.profile.nameRequired);
          return;
        }
        const offers =
          offer.trim().length === 0
            ? payload.offers
            : firstOffer === undefined
              ? [newOffer(offer, '')]
              : [
                  { ...firstOffer, title: confirmedField(firstOffer.title, offer) },
                  ...payload.offers.slice(1),
                ];
        onSave({
          ...payload,
          identity: confirmedField(payload.identity, name),
          niche: confirmedField(payload.niche, description),
          geography: confirmedField(payload.geography, geography),
          offers,
        });
      }}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {copy.common.cancel}
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? copy.profile.saving : copy.profile.save}
          </Button>
        </>
      }
    >
      <Field label={copy.profile.businessName} id={ids.name} error={error}>
        <input
          id={ids.name}
          className="mj-ws-inp"
          data-autofocus="1"
          value={name}
          aria-invalid={error !== null}
          onChange={(event) => {
            setName(event.target.value);
            setError(null);
          }}
        />
      </Field>
      <Field label={copy.profile.description} id={ids.desc}>
        <textarea
          id={ids.desc}
          className="mj-ws-inp"
          rows={3}
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
      </Field>
      <div className="mj-ws-field">
        <GeographyCombobox
          label={copy.profile.geography}
          value={geography}
          invalid={false}
          onChange={setGeography}
        />
        <span className="mj-ws-vh" lang={language} />
      </div>
      <Field label={copy.line.offer} id={ids.offer}>
        <input
          id={ids.offer}
          className="mj-ws-inp"
          value={offer}
          placeholder={copy.profile.offerNamePlaceholder}
          onChange={(event) => {
            setOffer(event.target.value);
          }}
        />
      </Field>
    </Modal>
  );
}

function OfferModal({
  copy,
  busy,
  offer,
  onClose,
  onSave,
}: {
  readonly copy: WorkspaceCopy;
  readonly busy: boolean;
  readonly offer: BusinessProfileOfferDraft | null;
  readonly onClose: () => void;
  readonly onSave: (title: string, description: string) => void;
}): ReactNode {
  const ids = { name: useId(), desc: useId() };
  const [name, setName] = useState(offer?.title.value ?? '');
  const [description, setDescription] = useState(offer?.description.value ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title={offer === null ? copy.profile.addOffer : copy.profile.editOffer}
      onClose={onClose}
      onSubmit={() => {
        if (name.trim().length === 0) {
          setError(copy.profile.offerNameRequired);
          return;
        }
        onSave(name.trim(), description.trim());
      }}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {copy.common.cancel}
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy
              ? copy.profile.saving
              : offer === null
                ? copy.profile.addOffer
                : copy.profile.saveOffer}
          </Button>
        </>
      }
    >
      <Field label={copy.profile.offerName} id={ids.name} error={error}>
        <input
          id={ids.name}
          className="mj-ws-inp"
          data-autofocus="1"
          value={name}
          placeholder={copy.profile.offerNamePlaceholder}
          aria-invalid={error !== null}
          onChange={(event) => {
            setName(event.target.value);
            setError(null);
          }}
        />
      </Field>
      <Field
        label={copy.profile.offerDesc}
        id={ids.desc}
        optional={copy.newLine.optional}
      >
        <input
          id={ids.desc}
          className="mj-ws-inp"
          value={description}
          placeholder={copy.profile.offerDescPlaceholder}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
      </Field>
    </Modal>
  );
}
